import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Save, Flag, Terminal, Timer, ShieldCheck, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead, PremiumBanner } from "@/components/kit";
import { RoleSelect } from "@/components/RoleSelect";

const FLAG_EXAMPLES = ["🇫🇷", "🇨🇦", "🇪🇸", "🇧🇷", "🇩🇪", "🇮🇹", "🇷🇺", "🇯🇵", "🇰🇷", "🇨🇳", "🇸🇦", "🇮🇳", "🇳🇱", "🇹🇷", "🇸🇪", "🇬🇧", "🇻🇳", "🇹🇭", "🇵🇱", "🇺🇦"];

export default function Translation() {
  const { guildId, guild, refreshKey } = useGuild();
  const locked = (guild?.plan || "free") === "free";
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());

  useEffect(() => {
    api.get(`/guilds/${guildId}/modules/translation`).then((r) => setCfg(r.data)).catch(() => {});
  }, [guildId, refreshKey]);

  const saveRaw = async () => {
    try {
      await api.put(`/guilds/${guildId}/modules/translation`, { enabled: cfg.enabled, config: cfg.config });
      toast.success("Translation settings saved");
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  if (!cfg) return null;
  const conf = cfg.config || {};
  const bypassRoles = conf.cooldown_bypass_roles || [];
  const addBypassRole = (v) => {
    const r = (v || "").trim().replace(/^@/, "");
    if (!r || bypassRoles.includes(r)) return;
    setCfg({ ...cfg, config: { ...conf, cooldown_bypass_roles: [...bypassRoles, r] } });
  };
  const removeBypassRole = (r) => setCfg({ ...cfg, config: { ...conf, cooldown_bypass_roles: bypassRoles.filter((x) => x !== r) } });

  return (
    <div data-testid="translation-page">
      <PageHead title="Translation" desc="Real-time translation across 100+ languages, powered by a built-in translator." />
      <PremiumBanner guildId={guildId} tier="Premium" locked={locked} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Languages className="w-5 h-5 text-volt" />
                <div>
                  <div className="text-sm font-semibold">Enable Translation</div>
                  <div className="text-xs text-zinc-500">Turn the whole module on or off for this server.</div>
                </div>
              </div>
              <Toggle testid="translation-enabled" checked={!!cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} />
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-volt" />
                <div>
                  <div className="text-sm font-semibold">Flag reactions</div>
                  <div className="text-xs text-zinc-500">Members react to any message with a country flag → the bot replies with the translation.</div>
                </div>
              </div>
              <Toggle testid="translation-flag-reactions" checked={conf.flag_reactions !== false} onChange={(v) => setCfg({ ...cfg, config: { ...conf, flag_reactions: v } })} />
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {FLAG_EXAMPLES.map((f) => <span key={f} className="text-lg bg-black/40 rounded-md px-2 py-1 border border-zinc-800">{f}</span>)}
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <Timer className="w-5 h-5 text-volt" />
                <div>
                  <div className="text-sm font-semibold">Per-user cooldown</div>
                  <div className="text-xs text-zinc-500">Minimum seconds between translations per member — stops flag-spam hammering the translator. Set 0 to disable.</div>
                </div>
              </div>
              <div className="shrink-0">
                <div className="flex items-center gap-2">
                  <input data-testid="translation-cooldown" type="number" min="0" max="300"
                    value={conf.cooldown_seconds ?? 5}
                    onChange={(e) => setCfg({ ...cfg, config: { ...conf, cooldown_seconds: Math.max(0, Math.min(300, +e.target.value)) } })}
                    className="w-20 bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm text-center outline-none focus:border-volt" />
                  <span className="text-xs text-zinc-500">sec</span>
                </div>
              </div>
            </div>

            <div className="mt-4 border-t border-zinc-800 pt-4">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-volt" />
                <div className="text-sm font-semibold">Bypass roles</div>
              </div>
              <div className="text-xs text-zinc-500 mb-2">Members with any of these roles (e.g. mods or boosters) skip the cooldown entirely.</div>
              <RoleSelect guildId={guildId} value="" onChange={addBypassRole} testid="translation-bypass-role" placeholder="— add a role —" className="w-full sm:w-72" />
              {bypassRoles.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {bypassRoles.map((r) => (
                    <span key={r} data-testid="translation-bypass-chip" className="inline-flex items-center gap-1 text-xs bg-volt/10 text-volt border border-volt/30 rounded-full pl-2.5 pr-1.5 py-1">
                      @{r}
                      <button onClick={() => removeBypassRole(r)} className="hover:bg-volt/20 rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button data-testid="translation-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2.5 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save settings</button>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 h-fit space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><Terminal className="w-4 h-4 text-volt" /> How members use it</h3>
          <div className="text-sm text-zinc-400 space-y-3">
            <div>
              <div className="text-zinc-200 font-medium mb-1">Slash command</div>
              <code className="block bg-black/50 border border-zinc-800 rounded-md px-3 py-2 text-xs font-mono text-volt">/translate language:spanish text:Hello everyone!</code>
              <p className="text-xs text-zinc-500 mt-1">Reply is private (only the user sees it). Accepts language names (<span className="font-mono">french</span>) or codes (<span className="font-mono">fr</span>).</p>
            </div>
            <div>
              <div className="text-zinc-200 font-medium mb-1">Flag reactions</div>
              <p className="text-xs text-zinc-500">React to any message with a flag like 🇫🇷 or 🇯🇵 — the bot replies with the translation in that language. Now supports 150+ country & regional flags, and multilingual countries map to their main language (e.g. 🇨🇦 → English, 🇧🇷 → Portuguese, 🇨🇭 → German). Works in 100+ languages with automatic source detection.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
